import { AbstractControl } from '@angular/forms';
import { Lang } from '../services/lang/lang.types';

// user-facing
export interface FormFieldLabelsConfig {
  label?: string;
  placeholder?: string;
  help?: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  errors?: Record<string, string | ((error: any) => string)>; // any because ValidationErrors is defined as  type ValidationErrors = {  [key: string]: any; }
  options?: ({ value: string; label?: string } | string)[];
  disabledTooltip?: string;
}

// implementation-facing
export interface FormFieldLabels {
  label?: string;
  placeholder?: string;
  help?: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  errors?: Record<string, (error: any) => string>; // any because ValidationErrors is defined as  type ValidationErrors = {  [key: string]: any; }
  options?: { value: string; label: string }[];
  disabledTooltip?: string;
}

export type FormFieldsLabelsConfig = Record<
  string,
  ((lang: Lang) => FormFieldLabelsConfig) | FormFieldLabelsConfig
>;

export enum FormFieldType {
  text = 'text',
  select = 'select',
  checkbox = 'checkbox',
  autocomplete = 'autocomplete',
  datePicker = 'datePicker',
  keyUsages = 'keyUsages',
  extendedKeyUsages = 'extendedKeyUsages',
}

interface FormFieldOptionsBase {
  defaultLabels?:
    | ((lang: Lang) => Partial<FormFieldLabelsConfig>)
    | Partial<FormFieldLabelsConfig>;
  required?: boolean;
  configureControl?: (control: AbstractControl) => void;
}

export interface TextFormFieldOptions extends FormFieldOptionsBase {
  inputType?: string;
  min?: number;
  max?: number;
  step?: number;
}

export interface SelectFormFieldOptions extends FormFieldOptionsBase {
  useEmpty?: boolean;
  emptyLabel?: string | ((lang: Lang) => string);
}

export interface CheckboxFormFieldOptions extends FormFieldOptionsBase {
  labelPosition?: 'before' | 'after';
}

export type CountryFormFieldOptions = Omit<
  AutocompleteFormFieldOptions,
  'options' | 'defaultLabels'
>;

export interface AutocompleteFormFieldOptions extends FormFieldOptionsBase {
  predicate?: (controlValue: string, option: string) => boolean;
  autoActiveFirstOption?: boolean;
  strict?: boolean;
}

export interface DatePickerFormFieldOptions extends FormFieldOptionsBase {
  inputType?: string;
  min?: Date;
  max?: Date;
  startAt?: Date;
  selectMode?: 'single' | 'range' | 'rangeFrom' | 'rangeTo';
  // More options can be imported as needed from https://www.npmjs.com/package/@lukana/ng-pick-datetime
  // Reasonable defaults should be provided by the component.
}

export interface KeyUsagesFormFieldOptions extends FormFieldOptionsBase {
  open?: boolean;
}

export interface ExtendedKeyUsagesFormFieldOptions
  extends FormFieldOptionsBase {
  open?: boolean;
}

export type FormFieldOptions =
  | ({ formFieldType: FormFieldType.text } & TextFormFieldOptions)
  | ({ formFieldType: FormFieldType.select } & SelectFormFieldOptions)
  | ({ formFieldType: FormFieldType.checkbox } & CheckboxFormFieldOptions)
  | ({
      formFieldType: FormFieldType.autocomplete;
    } & AutocompleteFormFieldOptions)
  | ({ formFieldType: FormFieldType.datePicker } & DatePickerFormFieldOptions)
  | ({ formFieldType: FormFieldType.keyUsages } & KeyUsagesFormFieldOptions)
  | ({
      formFieldType: FormFieldType.extendedKeyUsages;
    } & ExtendedKeyUsagesFormFieldOptions);

export const FormFieldOptions = {
  text: (options?: TextFormFieldOptions): FormFieldOptions => ({
    formFieldType: FormFieldType.text,
    ...(options ?? {}),
  }),

  select: (options?: SelectFormFieldOptions): FormFieldOptions => ({
    formFieldType: FormFieldType.select,
    ...(options ?? {}),
  }),

  checkbox: (options?: CheckboxFormFieldOptions): FormFieldOptions => ({
    formFieldType: FormFieldType.checkbox,
    ...(options ?? {}),
  }),

  autocomplete: (options?: AutocompleteFormFieldOptions): FormFieldOptions => ({
    formFieldType: FormFieldType.autocomplete,
    ...(options ?? {}),
  }),

  datePicker: (options?: DatePickerFormFieldOptions): FormFieldOptions => ({
    formFieldType: FormFieldType.datePicker,
    ...(options ?? {}),
  }),

  keyUsages: (options?: KeyUsagesFormFieldOptions): FormFieldOptions => ({
    formFieldType: FormFieldType.keyUsages,
    ...(options ?? {}),
  }),

  extendedKeyUsages: (
    options?: ExtendedKeyUsagesFormFieldOptions,
  ): FormFieldOptions => ({
    formFieldType: FormFieldType.extendedKeyUsages,
    ...(options ?? {}),
  }),

  country: (options?: CountryFormFieldOptions): FormFieldOptions =>
    FormFieldOptions.autocomplete({
      defaultLabels: lang => lang.shared.standardInputs.country,
      strict: true,
      ...(options ?? {}),
    }),

  usState: (options?: CountryFormFieldOptions): FormFieldOptions =>
    FormFieldOptions.autocomplete({
      defaultLabels: lang => lang.shared.standardInputs.usState,
      strict: true,
      ...(options ?? {}),
    }),
};
